import {
    useWindowDimensions,
    View
} from "react-native";
import type StatsGridProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "ncore-ui-kit";
import {
    CalendarDaysIcon,
    NewspaperIcon,
    Building2Icon,
    HardDriveIcon,
    UsersIcon
} from "lucide-react-native";
import StatCard from "../statCard";

const StatsGrid = ({
    onNavigateToCompanies,
    onNavigateToEvents,
    onNavigateToDrive,
    onNavigateToUsers,
    onNavigateToNews,
    stats
}: StatsGridProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;
    const isTablet = width < 1200;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const formatBytes = (bytes: number): string => {
        if(!bytes || bytes <= 0) {
            return "0 B";
        }

        const k = 1024;
        const sizes = [
            "B",
            "KB",
            "MB",
            "GB",
            "TB"
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    };

    const cardMinWidth = isMobile ? "100%" : isTablet ? "47%" : "30%";

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={[
                stylesheet.gridRow,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.cardWrapper,
                    {
                        minWidth: cardMinWidth
                    }
                ]}
            >
                <StatCard
                    subStats={[
                        {
                            label: localize("dashboard-stat-active-users"),
                            value: stats.users.active
                        },
                        {
                            label: localize("dashboard-stat-student-users"),
                            value: stats.users.students
                        },
                        {
                            label: localize("dashboard-stat-pending-users"),
                            value: stats.users.pendingVerification
                        }
                    ]}
                    title={localize("dashboard-stat-total-users")}
                    onPress={onNavigateToUsers}
                    value={stats.users.total}
                    icon={UsersIcon}
                />
            </View>
            <View
                style={[
                    stylesheet.cardWrapper,
                    {
                        minWidth: cardMinWidth
                    }
                ]}
            >
                <StatCard
                    subStats={[
                        {
                            label: localize("dashboard-stat-exporting-companies"),
                            value: stats.companies.exporting
                        }
                    ]}
                    title={localize("dashboard-stat-total-companies")}
                    onPress={onNavigateToCompanies}
                    value={stats.companies.total}
                    icon={Building2Icon}
                />
            </View>
            <View
                style={[
                    stylesheet.cardWrapper,
                    {
                        minWidth: cardMinWidth
                    }
                ]}
            >
                <StatCard
                    subStats={[
                        {
                            label: localize("dashboard-stat-active-events"),
                            value: stats.events.active
                        },
                        {
                            label: localize("dashboard-stat-event-participants"),
                            value: stats.events.totalParticipants
                        }
                    ]}
                    title={localize("dashboard-stat-total-events")}
                    onPress={onNavigateToEvents}
                    value={stats.events.total}
                    icon={CalendarDaysIcon}
                />
            </View>
            <View
                style={[
                    stylesheet.cardWrapper,
                    {
                        minWidth: cardMinWidth
                    }
                ]}
            >
                <StatCard
                    title={`${localize("dashboard-stat-news")} & ${localize("dashboard-stat-announcements")}`}
                    subStats={[
                        {
                            value: `${stats.content.activeNews}/${stats.content.totalNews}`,
                            label: localize("dashboard-stat-news")
                        },
                        {
                            value: `${stats.content.activeAnnouncements}/${stats.content.totalAnnouncements}`,
                            label: localize("dashboard-stat-announcements")
                        }
                    ]}
                    value={stats.content.totalNews + stats.content.totalAnnouncements}
                    onPress={onNavigateToNews}
                    icon={NewspaperIcon}
                />
            </View>
            <View
                style={[
                    stylesheet.cardWrapper,
                    {
                        minWidth: cardMinWidth
                    }
                ]}
            >
                <StatCard
                    subStats={[
                        {
                            value: formatBytes(stats.storage.totalSizeBytes),
                            label: localize("dashboard-stat-storage-size")
                        }
                    ]}
                    title={localize("dashboard-stat-total-storage")}
                    value={stats.storage.totalFiles}
                    onPress={onNavigateToDrive}
                    icon={HardDriveIcon}
                />
            </View>
        </View>
    </View>;
};
export default StatsGrid;
